import React, { useEffect, useState } from "react";
import api from "../services/api";
import { blockReason, useConsole } from "../lib/console";
import { attackLabel, attackVar, duration, fmtDateTime, num, plural, sevVar } from "../lib/format";
import {
  Button,
  Callout,
  CopyButton,
  CountryTag,
  Drawer,
  Empty,
  ErrorState,
  KV,
  LinkButton,
  Modal,
  SevBadge,
  SkeletonRows,
  cx,
} from "./ui";
import { Icon } from "./Icon";
import { BarList } from "./charts";

const LEVEL_TONE = { CRITICAL: "critical", HIGH: "high", MEDIUM: "medium", LOW: "low", INFO: "info" };

function Reputation({ ip }) {
  const [state, setState] = useState({ loading: false, intel: null, feed: null, error: null });
  const run = async () => {
    setState({ loading: true, intel: null, feed: null, error: null });
    const [intel, feed] = await Promise.allSettled([api.get(`/api/threat-intel/ip/${ip}`), api.get(`/api/feeds/lookup/${encodeURIComponent(ip)}`)]);
    setState({
      loading: false,
      intel: intel.status === "fulfilled" ? intel.value.data : null,
      feed: feed.status === "fulfilled" ? feed.value.data : null,
      error: intel.status === "rejected" && feed.status === "rejected" ? "Lookup failed" : null,
    });
  };
  const { loading, intel, feed, error } = state;
  if (!intel && !feed && !error)
    return (
      <div className="row-between row-wrap">
        <p className="small dim" style={{ maxWidth: 340 }}>
          Check this address against public abuse feeds and any configured reputation providers.
        </p>
        <Button size="sm" icon="search" loading={loading} onClick={run}>
          Check reputation
        </Button>
      </div>
    );
  if (error) return <ErrorState title="Reputation lookup failed" detail="The intelligence services did not respond." onRetry={run} />;
  const providers = intel?.sources || [];
  return (
    <div className="col" style={{ gap: 12 }}>
      {feed?.matched ? (
        <Callout tone="critical" icon="alert" title={`Listed in ${feed.ioc?.source || "a public feed"}`}>
          {[feed.ioc?.malware, feed.ioc?.description].filter(Boolean).join(" · ") || "Known malicious indicator."}
        </Callout>
      ) : feed ? (
        <Callout tone="ok" icon="checkCircle">
          Not listed in the loaded public threat feeds.
        </Callout>
      ) : null}
      {providers.length ? (
        <KV
          items={[
            ["Providers", providers.join(", ")],
            ["Reputation score", `${intel.threat_score ?? 0} / 100`],
            intel.vt_total != null && ["VirusTotal", `${intel.vt_malicious ?? 0} malicious of ${intel.vt_total} engines`],
            intel.abuse_confidence != null && ["AbuseIPDB confidence", `${intel.abuse_confidence}% · ${intel.abuse_reports ?? 0} reports`],
          ]}
        />
      ) : (
        <p className="small muted">No reputation provider returned data for this address (provider API keys may not be configured).</p>
      )}
    </div>
  );
}

export default function AttackerProfile({ ip, onClose }) {
  const c = useConsole();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!ip) return undefined;
    let alive = true;
    setData(null);
    setError(null);
    api
      .get(`/api/attackers/${encodeURIComponent(ip)}/profile`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.status === 404 ? "notfound" : e?.response?.data?.detail || e.message));
    return () => {
      alive = false;
    };
  }, [ip, nonce]);

  if (!ip) return null;
  const blocked = c.blockedIPs.has(ip) || data?.is_blocked;
  const block = data?.block || {};

  const act = async () => {
    setBusy(true);
    const ok = confirm === "block" ? await c.blockIP(ip) : await c.allowIP(ip);
    setBusy(false);
    setConfirm(null);
    if (ok) setNonce((n) => n + 1);
  };

  const types = data
    ? Object.entries(data.attack_types || {}).map(([k, v]) => ({ key: k, label: attackLabel(k), value: v, color: attackVar(k) }))
    : null;
  const device = data?.device || {};
  const hasDevice = ["os", "browser", "device_type", "tool"].some((k) => device[k] && device[k] !== "Unknown");

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        width={620}
        label={`Source profile ${ip}`}
        title={
          <span className="row" style={{ gap: 6 }}>
            <span className="mono">{ip}</span>
            <CopyButton value={ip} label="Copy IP address" />
          </span>
        }
        subtitle={
          data
            ? [[data.city, data.country].filter(Boolean).join(", "), data.isp || data.org].filter(Boolean).join(" · ") || "Location unknown"
            : "Source profile"
        }
        icon={
          <span className={cx("sev-glyph", data && `tone-${LEVEL_TONE[data.risk_level] || "info"}`)}>
            <Icon name="user" size={17} />
          </span>
        }
        footer={
          <>
            <LinkButton href={`/api/attackers/${encodeURIComponent(ip)}/report`} target="_blank" rel="noreferrer" variant="ghost" icon="download">
              PDF dossier
            </LinkButton>
            <Button icon="crosshair" onClick={() => c.hunt({ source_ip: ip })}>
              Hunt
            </Button>
            {blocked ? (
              <Button variant="success" icon="unlock" onClick={() => setConfirm("allow")}>
                Release source
              </Button>
            ) : (
              <Button variant="danger" icon="ban" onClick={() => setConfirm("block")}>
                Block source
              </Button>
            )}
          </>
        }
      >
        {error === "notfound" ? (
          <Empty icon="search" title="No stored detections for this source">
            The address has not been recorded in the detection database yet. It may be a very recent or non-threat event.
          </Empty>
        ) : error ? (
          <ErrorState detail={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : !data ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            <div className="card-inset" style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 20, alignItems: "center" }}>
              <div style={{ textAlign: "center", minWidth: 96 }}>
                <div style={{ fontSize: 40, fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1, color: sevVar(data.risk_level === "INFO" ? "INFO" : data.risk_level) }}>
                  {data.risk_score}
                </div>
                <div className="tiny muted" style={{ marginTop: 4 }}>
                  source risk / 100
                </div>
                <div style={{ marginTop: 8 }}>
                  <SevBadge sev={data.risk_level} />
                </div>
              </div>
              <div className="col" style={{ gap: 8 }}>
                <div className="small muted">What drives this score</div>
                {(data.risk_factors || []).map((f) => (
                  <div key={f.label} className="row-between small">
                    <span className="dim">{f.label}</span>
                    <span className="tnum strong">+{f.points}</span>
                  </div>
                ))}
                {!data.risk_factors?.length && <span className="small muted">No scored activity.</span>}
              </div>
            </div>

            <div className="drawer-section">
              <h3>Response status</h3>
              {blocked ? (
                <>
                  <Callout tone="ok" icon="shield" title={`Blocked · ${blockReason(block.reason).label}`}>
                    {blockReason(block.reason).detail}
                    {block.trigger && block.reason !== "manual" ? ` · triggered by ${attackLabel(block.trigger)}` : ""}
                  </Callout>
                  <div style={{ marginTop: 14 }}>
                    <KV
                      items={[
                        ["Blocked at", block.blocked_at ? `${fmtDateTime(block.blocked_at)} GST` : "—"],
                        ["Expires", block.expires_in_s != null ? `in ${duration(block.expires_in_s)}` : "Until released"],
                        ["Detections before block", num(block.events_before_block)],
                        ["Attempts dropped since", num(block.dropped_after_block)],
                      ]}
                    />
                  </div>
                </>
              ) : (
                <Callout tone={data.risk_level === "CRITICAL" || data.risk_level === "HIGH" ? "medium" : "info"} icon="info" title="Not blocked">
                  {block.released_at
                    ? `Released ${fmtDateTime(block.released_at)} GST${block.released_why ? ` (${block.released_why})` : ""}.`
                    : "Traffic from this source currently reaches the platform."}
                </Callout>
              )}
            </div>

            <div className="drawer-section">
              <h3>Activity</h3>
              <KV
                items={[
                  ["First seen", `${fmtDateTime(data.first_seen)} GST`],
                  ["Last seen", `${fmtDateTime(data.last_seen)} GST`],
                  ["Confirmed attacks", num(data.total_attacks)],
                  ["Anomalies only", num(data.anomalies)],
                  ["Classification", data.classification === "confirmed_attacker" ? "Confirmed attacker" : "Anomalous source"],
                  ["Network", data.vpn_detected ? `VPN / proxy${data.vpn_type ? ` (${data.vpn_type})` : ""}` : data.is_mobile ? "Mobile carrier" : "Direct"],
                ]}
              />
              <div style={{ marginTop: 18 }}>
                <BarList items={types} empty="No attack types recorded." />
              </div>
            </div>

            <div className="drawer-section">
              <h3>Recent detections</h3>
              <div className="list" style={{ margin: "0 -22px" }}>
                {(data.recent_events || []).map((e) => (
                  <div key={e.id} className="list-item" style={{ padding: "10px 22px", gridTemplateColumns: "auto minmax(0,1fr) auto" }}>
                    <span className="sev-bar" style={{ "--tone": sevVar(e.severity) }} />
                    <span style={{ minWidth: 0 }}>
                      <span className="list-title" style={{ display: "block", fontSize: 13 }}>
                        {attackLabel(e.attack_type)}
                      </span>
                      <span className="list-meta">
                        <span>{fmtDateTime(e.timestamp)}</span>
                        {e.mitre_technique_id && <span className="mono">{e.mitre_technique_id}</span>}
                      </span>
                    </span>
                    <SevBadge sev={e.severity} />
                  </div>
                ))}
              </div>
            </div>

            {hasDevice && (
              <div className="drawer-section">
                <h3>Device fingerprint</h3>
                <KV
                  items={[
                    ["Operating system", device.os],
                    ["Browser", device.browser],
                    ["Device type", device.device_type],
                    device.tool && ["Tool", device.tool],
                    device.timezone && ["Time zone", device.timezone],
                    device.language && ["Language", device.language],
                    device.screen && ["Screen", device.screen],
                    device.cpu_cores ? ["Hardware", `${device.cpu_cores} cores${device.ram_gb ? ` · ${device.ram_gb} GB` : ""}`] : null,
                  ]}
                />
              </div>
            )}

            <div className="drawer-section">
              <h3>Reputation</h3>
              <Reputation ip={ip} />
            </div>
          </>
        )}
      </Drawer>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} label={confirm === "block" ? "Confirm block" : "Confirm release"}>
        <div className="modal-head">
          <h2>{confirm === "block" ? `Block ${ip}?` : `Release ${ip}?`}</h2>
          <p>
            {confirm === "block"
              ? "All further traffic from this address is dropped until an analyst releases it. Manual blocks do not expire."
              : "The address will be able to reach the platform again. New detections can block it again automatically."}
          </p>
        </div>
        <div className="modal-body">
          {data && (
            <div className="small dim">
              {plural(data.total_attacks || 0, "confirmed attack")} recorded · source risk {data.risk_score}/100
            </div>
          )}
        </div>
        <div className="modal-foot">
          <Button variant="ghost" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button variant={confirm === "block" ? "danger" : "success"} loading={busy} icon={confirm === "block" ? "ban" : "unlock"} onClick={act} data-autofocus>
            {confirm === "block" ? "Block source" : "Release source"}
          </Button>
        </div>
      </Modal>
    </>
  );
}
